import * as React from "react";
import { useMemo } from "react";
import { Route, RouteComponentProps, Switch } from "react-router-dom";
import { getRedirectForPlayer, URLS, useAccountSettings } from "./utils";
import { useAnalyticsPageView } from "./utils/gtag";
import { RouteType, Types } from "@socialcoach/services";
import {
  AddPlayer,
  AdminLeaderboards,
  AlertsSection,
  CalendarPage,
  CampaignPage,
  Campaigns,
  EditCampaignPage,
  CreateAssistant,
  EditAssistant,
  EditPlayer,
  FirstLoginPage,
  SetPlayerPicture,
  FirstSocialNetworksPage,
  SettingsSection,
  PaymentInfo,
  FirstCongratsPage,
  PlayerSettings,
  EditorVideoProjectPage,
  ChangePasswordForm,
  CompliancePage,
  Dashboard,
  EditorEditingQueueList,
  EmbeddedPlayerSocialAccounts,
  Home,
  Library,
  LoginPage,
  SSOLoginPage,
  NotFoundPage,
  Users,
  PlayerVideoProjectPage,
  PlayerVideoScriptPage,
  RecordVideoPage,
  ResendPage,
  PlayerCardSettings,
  VerificationPage,
  VideoProjectDetailsPage,
  VideoScriptPage,
  VideoScriptsPage,
  PlayerDashboard,
  PlayerLibrary,
  UsersPage,
  PkceAuthorization,
  ScreenCapturePage,
  ReviewResponsePage,
} from "./containers";

import { AppContextProvider, AppContext, AppUserContext } from "./providers";
import { CoachSettings, ConfigProperties, PricePlanTier, UserGroups, WebComponents, WebSettings } from "./interfaces";
import MetaTags from "./components/MetaTags";

import { SCSessionRoute } from "./routers/SessionRoute";
import { LegacyRedirectRoute } from "./routers/LegacyRedirectRoute";
import { getLegacyRedirectRules } from "./legacy/legacyRedirects";

import { Footer } from "./components/";
import { get } from "lodash";
import { usePlayerSettings } from "./utils";
import { ConfigContext } from "./contexts/appContexts";
import { syncUtilitiesContext } from "./utils/syncUtilitiesContext";
import { Reputation } from "@/containers/Coach/Reputation";
import { PlayerReputation } from "@/containers/Players/Reputation";

const UtilitiesContextSync: React.FC = () => {
  const { userContext } = React.useContext(AppContext);
  const { firebase } = React.useContext(ConfigContext);

  React.useEffect(() => {
    syncUtilitiesContext(userContext, firebase);
  }, [userContext, firebase]);
  return null;
};

interface PageProps extends RouteComponentProps {
  title?: string;
  includeHeader?: boolean;
  includeFooter?: boolean;
  Component: any;
  config?: ConfigProperties;
  type?: string;
  isPlayer?: boolean;
  [key: string]: any;
}

const Page = ({ location, title = "", includeHeader = true, includeFooter = true, Component, ...props }: PageProps) => {
  const { pathname, search } = location;
  useAnalyticsPageView(pathname + search);

  const pageTitle = useMemo(() => {
    return title ? `${title} - SocialCoach` : "SocialCoach";
  }, [title]);

  return (
    <div className="flex-1 flex flex-col h-full w-full relative">
      <MetaTags title={pageTitle} />

      <main className="flex-auto flex flex-col w-full">
        <Component {...props} />
      </main>

      {includeFooter && <Footer />}
    </div>
  );
};

export default (config: ConfigProperties) => {
  const getPkceAuthRedirectUrl = (): string | null => {
    if (typeof window === "undefined") return null;
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get("pkce") === "true") {
      searchParams.delete("pkce");
      return `${URLS.pkceAuth}?${searchParams.toString()}`;
    } else {
      return null;
    }
  };

  const userDefaultRedirectUrl = (userContext?: AppUserContext): string => {
    const isEditor = userContext?.account?.scGroups?.some(g => g === UserGroups.EDITOR);

    return isEditor
      ? URLS.editor.editQueueList
      : userContext?.coach
      ? URLS.coach.dashboard
      : getRedirectForPlayer(userContext!);
  };

  const validateCoachRedirectUrl = (userContext?: AppUserContext): string => {
    const isEditor = userContext?.account?.scGroups?.some(g => g === UserGroups.EDITOR);
    const isAdmin = userContext?.account.groups.some(g => g === UserGroups.ADMIN);

    return isEditor ? URLS.editor.editQueueList : isAdmin ? URLS.admin.dashboard : URLS.player.dashboard;
  };

  const validatePublicPageRedirectUrl = (userContext?: AppUserContext): string => {
    const isAdmin = userContext?.account.groups.some(g => g === UserGroups.ADMIN);
    const isCoachOrPlayer = userContext?.account.scGroups?.some(g => g === UserGroups.PLAYER || g === UserGroups.COACH);

    return isCoachOrPlayer
      ? userDefaultRedirectUrl(userContext!!)
      : isAdmin
      ? URLS.admin.dashboard
      : URLS.editor.editQueueList;
  };
  const validateEditorRedirectUrl = (userContext?: AppUserContext): string => {
    const isAdmin = userContext?.account.groups.some(g => g === UserGroups.ADMIN);

    return isAdmin
      ? URLS.admin.dashboard
      : userContext?.coach?.id != null
      ? URLS.coach.dashboard
      : URLS.player.dashboard;
  };

  const validatePlayerRedirectUrl = (userContext?: AppUserContext): string => {
    const isEditor = userContext?.account?.scGroups?.some(g => g === UserGroups.EDITOR);
    const isAdmin = userContext?.account.groups.some(g => g === UserGroups.ADMIN);

    return isEditor ? URLS.editor.editQueueList : isAdmin ? URLS.admin.dashboard : URLS.login;
  };

  const componentResult = (componentName: WebComponents | WebComponents[], Component: any, props: any) => {
    const { hasReadPermission } = useAccountSettings();
    const hasPermissions = hasReadPermission(componentName);
    return hasPermissions ? (
      <Page {...props} config={config} Component={Component} />
    ) : (
      <Page {...props} config={config} Component={NotFoundPage} />
    );
  };

  const scriptAccessComponentResult = (
    props: any,
    Component: any,
    userContext?: AppUserContext,
    additionalSettings?: CoachSettings[]
  ) => {
    const isAdmin = userContext?.account.groups.some(g => g === UserGroups.ADMIN);
    const isEditor = userContext?.account.scGroups?.some(g => g === UserGroups.EDITOR);
    const subscriptionType = get(userContext, "subscriptions[0].pricePlan.tier", undefined);
    const isSocialCoachPlus = subscriptionType === PricePlanTier.PLAYER_PLUS;
    const isPlayer = !!userContext?.player;

    const { hasAccountSetting } = useAccountSettings();
    const { hasSetting } = usePlayerSettings(userContext?.player?.id);
    const hasScriptAccess = useMemo(() => {
      return (
        isAdmin ||
        isEditor ||
        (!isPlayer && hasAccountSetting(WebSettings.MANAGE_SCRIPT_LIBRARY)) ||
        (isPlayer && hasAccountSetting(WebSettings.ACCESS_SCRIPT_LIBRARY)) ||
        (isPlayer && isSocialCoachPlus)
      );
    }, [isAdmin, hasAccountSetting, isEditor, isPlayer, isSocialCoachPlus]);
    const hasAdditionalSetting = useMemo(() => {
      if (isPlayer && additionalSettings && additionalSettings.length > 0) {
        return additionalSettings.every(setting => hasSetting(setting, true) !== false);
      } else {
        return true;
      }
    }, [hasSetting, additionalSettings, isPlayer]);

    return hasScriptAccess && hasAdditionalSetting ? (
      <Page {...props} config={config} Component={Component} />
    ) : (
      <Page {...props} config={config} Component={NotFoundPage} />
    );
  };

  return (
    <AppContextProvider>
      <UtilitiesContextSync />
      <AppContext.Consumer>
        {({ userContext }) => (
          <Switch>
            {/* ALL USER PAGES */}
            <SCSessionRoute
              routeType={RouteType.PUBLIC_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePublicPageRedirectUrl(userContext),
              }}
              exact
              path={URLS.forgotPassword}
              component={(props: RouteComponentProps) => (
                <Page {...props} type="ForgotPassword" includeHeader={false} Component={ResendPage} />
              )}
            />

            <SCSessionRoute
              routeType={RouteType.PUBLIC_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePublicPageRedirectUrl(userContext),
              }}
              exact
              path={URLS.changePassword}
              component={(props: RouteComponentProps) => (
                <Page {...props} includeHeader={false} Component={ChangePasswordForm} />
              )}
            />

            {/* ALL USER PAGES END */}

            {/* LEGACY REDIRECTS */}
            {getLegacyRedirectRules(userContext).map(rule => (
              <Route
                key={rule.path}
                exact
                path={rule.path}
                render={(routeProps: any) => (
                  <LegacyRedirectRoute
                    {...routeProps}
                    getTo={(userContext, rp) => {
                      const params = (rp.match?.params ?? {}) as Record<string, string>;
                      const to = rule.getTo(userContext, params);
                      return `${to}${rp.location.search ?? ""}${rp.location.hash ?? ""}`;
                    }}
                  />
                )}
              />
            ))}

            {/* COACH PAGES */}
            <SCSessionRoute
              routeType={RouteType.PUBLIC_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePublicPageRedirectUrl(userContext),
              }}
              exact
              path={URLS.home}
              component={(props: RouteComponentProps) => (
                <Page {...props} config={config} Component={Home} includeHeader={false} />
              )}
            />
            <SCSessionRoute
              routeType={RouteType.PUBLIC_ROUTE}
              exact
              redirection={{
                isExternal: false,
                url: getPkceAuthRedirectUrl() ?? validatePublicPageRedirectUrl(userContext),
              }}
              path={URLS.login}
              component={(props: RouteComponentProps) => (
                <Page
                  {...props}
                  Component={(pageProps: any) => <LoginPage {...pageProps} group={Types.UserGroups.USER} />}
                  includeHeader={false}
                />
              )}
            />
            <SCSessionRoute
              routeType={RouteType.PUBLIC_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePublicPageRedirectUrl(userContext),
              }}
              exact
              path={URLS.signInSSO}
              component={(props: RouteComponentProps) => (
                <Page {...props} Component={SSOLoginPage} includeHeader={false} />
              )}
            />
            <SCSessionRoute
              routeType={RouteType.PUBLIC_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePublicPageRedirectUrl(userContext),
              }}
              exact
              path={URLS.activatePlayerUser}
              component={(props: RouteComponentProps) => (
                <Page {...props} Component={VerificationPage} isPlayer={true} />
              )}
            />
            <SCSessionRoute
              routeType={RouteType.PUBLIC_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePublicPageRedirectUrl(userContext),
              }}
              exact
              path={URLS.activateUser}
              component={(props: RouteComponentProps) => (
                <Page {...props} Component={VerificationPage} includeHeader={false} />
              )}
            />
            <SCSessionRoute
              routeType={RouteType.PUBLIC_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePublicPageRedirectUrl(userContext),
              }}
              exact
              path={URLS.resendEmail}
              component={(props: RouteComponentProps) => <Page {...props} Component={ResendPage} />}
            />

            {/** PLAYER PRIVATE PAGES */}
            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePlayerRedirectUrl(userContext),
              }}
              exact
              path={URLS.player.dashboard}
              reservedGroups={["PLAYER"]}
              component={(props: RouteComponentProps) => <Page {...props} Component={PlayerDashboard} />}
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePlayerRedirectUrl(userContext),
              }}
              exact
              path={URLS.player.libraryTab}
              reservedGroups={["PLAYER"]}
              component={(props: RouteComponentProps) => <Page {...props} Component={PlayerLibrary} />}
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePlayerRedirectUrl(userContext),
              }}
              path={URLS.player.calendar}
              reservedGroups={["PLAYER"]}
              component={(props: RouteComponentProps) => <Page {...props} config={config} Component={CalendarPage} />}
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePlayerRedirectUrl(userContext),
              }}
              path={URLS.player.playerCards}
              reservedGroups={["PLAYER"]}
              component={(props: RouteComponentProps) => {
                const playerId = get(userContext, "player.id", undefined);
                const { hasSetting } = usePlayerSettings(playerId);
                const hasPlayerCardAccess = hasSetting(CoachSettings.ALLOW_PLAYER_ACCESS_TO_PLAYER_CARDS);

                if (hasPlayerCardAccess === false) {
                  return <Page {...props} config={config} Component={NotFoundPage} />;
                } else {
                  return (
                    <Page
                      {...props}
                      config={config}
                      Component={PlayerCardSettings}
                      pageLoading={hasPlayerCardAccess === undefined}
                    />
                  );
                }
              }}
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePlayerRedirectUrl(userContext),
              }}
              path={URLS.player.reputation.respond}
              reservedGroups={["PLAYER"]}
              component={(props: RouteComponentProps) => {
                const playerId = get(userContext, "player.id", undefined);
                const { hasSetting } = usePlayerSettings(playerId);
                const hasReputationAccess = hasSetting(CoachSettings.PLAYER_REPUTATION_MANAGEMENT);

                if (hasReputationAccess === false) {
                  return <Page {...props} config={config} Component={NotFoundPage} />;
                } else {
                  return (
                    <Page
                      {...props}
                      config={config}
                      Component={ReviewResponsePage}
                      includeHeader={false}
                      includeFooter={false}
                      pageLoading={hasReputationAccess === undefined}
                    />
                  );
                }
              }}
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePlayerRedirectUrl(userContext),
              }}
              path={URLS.player.reputation.tab}
              reservedGroups={["PLAYER"]}
              component={(props: RouteComponentProps) => {
                const playerId = get(userContext, "player.id", undefined);
                const { hasSetting } = usePlayerSettings(playerId);
                const hasReputationAccess = hasSetting(CoachSettings.PLAYER_REPUTATION_MANAGEMENT);

                if (hasReputationAccess === false) {
                  return <Page {...props} config={config} Component={NotFoundPage} />;
                } else {
                  return (
                    <Page
                      {...props}
                      config={config}
                      Component={PlayerReputation}
                      includeFooter={false}
                      pageLoading={hasReputationAccess === undefined}
                    />
                  );
                }
              }}
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePlayerRedirectUrl(userContext),
              }}
              exact
              path={URLS.player.firstLogin}
              reservedGroups={["PLAYER"]}
              component={(props: RouteComponentProps) =>
                !!userContext?.player ? (
                  <Page {...props} config={config} Component={FirstLoginPage} />
                ) : (
                  <Page {...props} config={config} Component={NotFoundPage} />
                )
              }
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePlayerRedirectUrl(userContext),
              }}
              exact
              path={URLS.player.firstCongrats}
              reservedGroups={["PLAYER"]}
              component={(props: RouteComponentProps) =>
                !!userContext?.player ? (
                  <Page {...props} config={config} Component={FirstCongratsPage} />
                ) : (
                  <Page {...props} config={config} Component={NotFoundPage} />
                )
              }
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePlayerRedirectUrl(userContext),
              }}
              exact
              path={URLS.player.firstPicture}
              reservedGroups={["PLAYER"]}
              component={(props: RouteComponentProps) => <Page {...props} Component={SetPlayerPicture} />}
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePlayerRedirectUrl(userContext),
              }}
              exact
              path={URLS.player.firstSocialNetworks}
              reservedGroups={["PLAYER"]}
              component={(props: RouteComponentProps) => <Page {...props} Component={FirstSocialNetworksPage} />}
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePlayerRedirectUrl(userContext),
              }}
              exact
              path={URLS.player.settings}
              reservedGroups={["PLAYER"]}
              component={(props: RouteComponentProps) => <Page {...props} Component={PlayerSettings} />}
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePlayerRedirectUrl(userContext),
              }}
              exact
              path={URLS.player.embeddedSocialAccounts}
              reservedGroups={["PLAYER"]}
              component={(props: RouteComponentProps) => (
                <Page {...props} Component={EmbeddedPlayerSocialAccounts} includeHeader={false} includeFooter={false} />
              )}
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePlayerRedirectUrl(userContext),
              }}
              exact
              path={URLS.player.paymentInfo}
              reservedGroups={["PLAYER"]}
              component={(props: RouteComponentProps) => <Page {...props} Component={PaymentInfo} />}
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: userContext?.account.groups.includes(Types.UserGroups.USER)
                  ? userDefaultRedirectUrl(userContext)
                  : URLS.admin.login,
              }}
              exact
              path={URLS.player.videoScript}
              reservedGroups={["PLAYER"]}
              component={(props: RouteComponentProps) =>
                scriptAccessComponentResult(props, PlayerVideoScriptPage, userContext)
              }
            />
            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: userContext?.account.groups.includes(Types.UserGroups.USER)
                  ? userDefaultRedirectUrl(userContext)
                  : URLS.admin.login,
              }}
              exact
              path={URLS.player.recordVideo}
              reservedGroups={["PLAYER"]}
              component={(props: RouteComponentProps) =>
                scriptAccessComponentResult({ ...props, includeHeader: false }, RecordVideoPage, userContext)
              }
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: userContext?.account.groups.includes(Types.UserGroups.USER)
                  ? userDefaultRedirectUrl(userContext)
                  : URLS.admin.login,
              }}
              exact
              path={URLS.player.screenCapture}
              reservedGroups={["PLAYER"]}
              component={(props: RouteComponentProps) =>
                scriptAccessComponentResult({ ...props, includeHeader: false }, ScreenCapturePage, userContext, [
                  CoachSettings.ALLOW_PLAYER_TO_SCREEN_CAPTURE,
                ])
              }
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: userContext?.account.groups.includes(Types.UserGroups.USER)
                  ? userDefaultRedirectUrl(userContext)
                  : URLS.admin.login,
              }}
              exact
              path={URLS.player.newVideoScript}
              reservedGroups={["PLAYER"]}
              component={(props: RouteComponentProps) =>
                scriptAccessComponentResult(props, PlayerVideoScriptPage, userContext)
              }
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              path={URLS.player.newVideoProject}
              reservedGroups={["PLAYER"]}
              component={(props: RouteComponentProps) => {
                const subscriptionType = get(userContext, "subscriptions[0].pricePlan.tier", undefined);
                const isSocialCoachPlus = subscriptionType === PricePlanTier.PLAYER_PLUS;

                return isSocialCoachPlus ? (
                  <Page {...props} config={config} Component={PlayerVideoProjectPage} />
                ) : (
                  <Page {...props} config={config} Component={NotFoundPage} />
                );
              }}
            />

            {/** COACH PRIVATE PAGES */}
            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              exact
              path={URLS.coach.dashboard}
              reservedGroups={["COACH"]}
              component={(props: RouteComponentProps) => <Page {...props} Component={Dashboard} />}
            />
            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              exact
              path={URLS.coach.alerts}
              reservedGroups={["COACH"]}
              component={(props: RouteComponentProps) => componentResult(WebComponents.ALERTS, AlertsSection, props)}
            />
            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              exact
              path={URLS.coach.newAssistant}
              reservedGroups={["COACH"]}
              component={(props: RouteComponentProps) =>
                componentResult(WebComponents.ASSISTANTS, CreateAssistant, props)
              }
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              exact
              path={URLS.coach.newPlayer}
              reservedGroups={["COACH"]}
              component={(props: RouteComponentProps) => componentResult(WebComponents.PLAYERS, AddPlayer, props)}
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              exact
              path={URLS.coach.editAssistant}
              reservedGroups={["COACH"]}
              component={(props: RouteComponentProps) =>
                componentResult(WebComponents.ASSISTANTS, EditAssistant, props)
              }
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              exact
              path={URLS.coach.libraryTabWithId}
              reservedGroups={["COACH"]}
              component={(props: RouteComponentProps) =>
                componentResult(
                  [
                    WebComponents.PROMPTS,
                    WebComponents.MANAGE_SCRIPT_LIBRARY,
                    WebComponents.VIDEO_CATALYST,
                    WebComponents.VIDEO_PAGES,
                  ],
                  Library,
                  props
                )
              }
            />
            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              exact
              path={URLS.coach.newCampaigns}
              reservedGroups={["COACH"]}
              component={(props: RouteComponentProps) => componentResult(WebComponents.CAMPAIGNS, CampaignPage, props)}
            />
            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              exact
              path={URLS.coach.campaigns}
              reservedGroups={["COACH"]}
              component={(props: RouteComponentProps) => componentResult(WebComponents.CAMPAIGNS, Campaigns, props)}
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              exact
              path={URLS.coach.editCampaigns}
              reservedGroups={["COACH"]}
              component={(props: RouteComponentProps) =>
                componentResult(WebComponents.CAMPAIGNS, EditCampaignPage, props)
              }
            />
            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              exact
              path={URLS.coach.usersTab}
              reservedGroups={["COACH"]}
              component={(props: RouteComponentProps) =>
                componentResult([WebComponents.PLAYERS, WebComponents.ASSISTANTS], Users, props)
              }
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              exact
              path={URLS.coach.reputation.tab}
              reservedGroups={["COACH"]}
              component={(props: RouteComponentProps) =>
                componentResult([WebComponents.REPUTATION_MANAGEMENT], Reputation, props)
              }
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              exact
              path={URLS.coach.editPlayerTab}
              reservedGroups={["COACH"]}
              component={(props: RouteComponentProps) => componentResult(WebComponents.PLAYERS, EditPlayer, props)}
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              exact
              path={URLS.coach.calendar}
              reservedGroups={["COACH"]}
              component={(props: RouteComponentProps) => <Page {...props} config={config} Component={CalendarPage} />}
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              exact
              path={URLS.coach.playerCards}
              reservedGroups={["COACH"]}
              component={(props: RouteComponentProps) => {
                const { hasReadPermission } = useAccountSettings();

                const hasAccess = useMemo(() => {
                  return hasReadPermission(WebComponents.PLAYER_CARDS);
                }, [hasReadPermission]);

                if (hasAccess !== false) {
                  return (
                    <Page
                      {...props}
                      config={config}
                      Component={PlayerCardSettings}
                      pageLoading={hasAccess === undefined}
                    />
                  );
                } else {
                  return <Page {...props} config={config} Component={NotFoundPage} />;
                }
              }}
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              exact
              path={URLS.coach.compliance}
              reservedGroups={["COACH"]}
              component={(props: RouteComponentProps) =>
                componentResult(WebComponents.COMPLIANCE, CompliancePage, props)
              }
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              exact
              path={URLS.coach.settingsTab}
              reservedGroups={["COACH"]}
              component={(props: RouteComponentProps) =>
                componentResult([WebComponents.APP_SETTINGS, WebComponents.API_KEYS], SettingsSection, props)
              }
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              exact
              path={URLS.coach.recordVideo}
              reservedGroups={["COACH"]}
              component={(props: RouteComponentProps) =>
                scriptAccessComponentResult({ ...props, includeHeader: false }, RecordVideoPage, userContext)
              }
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              exact
              path={URLS.coach.screenCapture}
              reservedGroups={["COACH"]}
              component={(props: RouteComponentProps) =>
                scriptAccessComponentResult({ ...props, includeHeader: false }, ScreenCapturePage, userContext)
              }
            />

            {/* COACH PAGES END*/}

            {/* COACH/PLAYER PAGES */}
            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateCoachRedirectUrl(userContext),
              }}
              exact
              path={URLS.player.videoProject}
              reservedGroups={["PLAYER"]}
              component={(props: RouteComponentProps) => {
                const hasAccess = useMemo(() => {
                  const subscriptionType = get(userContext, "subscriptions[0].pricePlan.tier", undefined);
                  const isSocialCoachPlus = subscriptionType === PricePlanTier.PLAYER_PLUS;
                  return isSocialCoachPlus;
                }, [userContext]);

                return hasAccess ? (
                  <Page {...props} config={config} Component={VideoProjectDetailsPage} />
                ) : (
                  <Page {...props} config={config} Component={NotFoundPage} />
                );
              }}
            />
            {/* COACH/PLAYER PAGES END*/}

            {/* ADMIN PAGES INIT*/}
            <SCSessionRoute
              routeType={RouteType.PUBLIC_ROUTE}
              exact
              redirection={{
                isExternal: false,
                url: userContext?.account.groups.includes(Types.UserGroups.USER)
                  ? userDefaultRedirectUrl(userContext)
                  : URLS.admin.dashboard,
              }}
              path={URLS.admin.login}
              reservedGroups={[Types.UserGroups.ADMIN]}
              component={(props: RouteComponentProps) => (
                <Page
                  {...props}
                  Component={(pageProps: any) => <LoginPage {...pageProps} group={Types.UserGroups.ADMIN} />}
                />
              )}
            />
            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: userContext?.account.groups.includes(Types.UserGroups.USER)
                  ? userDefaultRedirectUrl(userContext)
                  : URLS.admin.login,
              }}
              exact
              path={URLS.admin.dashboard}
              reservedGroups={[Types.UserGroups.ADMIN]}
              component={(props: RouteComponentProps) => <Page {...props} Component={UsersPage} />}
            />
            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: userContext?.account.groups.includes(Types.UserGroups.USER)
                  ? userDefaultRedirectUrl(userContext)
                  : URLS.login,
              }}
              exact
              path={URLS.admin.leaderboards}
              reservedGroups={[Types.UserGroups.ADMIN]}
              component={(props: RouteComponentProps) => <Page {...props} Component={AdminLeaderboards} />}
            />
            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: userContext?.account.groups.includes(Types.UserGroups.USER)
                  ? userDefaultRedirectUrl(userContext)
                  : URLS.admin.login,
              }}
              exact
              path={URLS.admin.coachCalendar}
              reservedGroups={[Types.UserGroups.ADMIN]}
              component={(props: RouteComponentProps) => <Page {...props} Component={CalendarPage} />}
            />
            {/* ADMIN PAGES END*/}

            {/* EDITOR PAGES */}
            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateEditorRedirectUrl(userContext),
              }}
              exact
              path={URLS.editor.editQueueList}
              reservedGroups={["EDITOR", "ADMIN"]}
              component={(props: RouteComponentProps) => (
                <Page {...props} config={config} Component={EditorEditingQueueList} />
              )}
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateEditorRedirectUrl(userContext),
              }}
              exact
              path={URLS.editor.editVideo}
              reservedGroups={["EDITOR", "ADMIN"]}
              component={(props: RouteComponentProps) => (
                <Page {...props} config={config} Component={EditorVideoProjectPage} />
              )}
            />
            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateEditorRedirectUrl(userContext),
              }}
              exact
              path={URLS.editor.videoScripts}
              reservedGroups={["EDITOR", "ADMIN"]}
              component={(props: RouteComponentProps) => (
                <Page {...props} config={config} Component={VideoScriptsPage} />
              )}
            />
            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validateEditorRedirectUrl(userContext),
              }}
              exact
              path={URLS.editor.videoScript}
              reservedGroups={["EDITOR", "ADMIN"]}
              component={(props: RouteComponentProps) => (
                <Page {...props} config={config} Component={VideoScriptPage} />
              )}
            />
            {/* EDITOR PAGES END */}

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: userContext?.account.groups.includes(Types.UserGroups.USER)
                  ? userDefaultRedirectUrl(userContext)
                  : URLS.admin.login,
              }}
              exact
              path={URLS.videoScripts}
              reservedGroups={[Types.UserGroups.ADMIN]}
              component={(props: RouteComponentProps) =>
                scriptAccessComponentResult(props, VideoScriptsPage, userContext)
              }
            />
            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: userContext?.account.groups.includes(Types.UserGroups.USER)
                  ? userDefaultRedirectUrl(userContext)
                  : URLS.admin.login,
              }}
              exact
              path={URLS.videoScript}
              reservedGroups={[Types.UserGroups.ADMIN]}
              component={(props: RouteComponentProps) =>
                scriptAccessComponentResult(props, VideoScriptPage, userContext)
              }
            />
            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: userContext?.account.groups.includes(Types.UserGroups.USER)
                  ? userDefaultRedirectUrl(userContext)
                  : URLS.admin.login,
              }}
              exact
              path={URLS.newVideoScript}
              reservedGroups={[Types.UserGroups.ADMIN]}
              component={(props: RouteComponentProps) =>
                scriptAccessComponentResult(props, VideoScriptPage, userContext)
              }
            />

            <SCSessionRoute
              routeType={RouteType.PRIVATE_ROUTE}
              redirection={{
                isExternal: false,
                url: validatePlayerRedirectUrl(userContext),
              }}
              exact
              path={URLS.pkceAuth}
              reservedGroups={[Types.UserGroups.ADMIN, Types.UserGroups.USER, "COACH", "PLAYER", "EDITOR"]}
              component={(props: RouteComponentProps) => (
                <Page
                  {...props}
                  Component={(pageProps: any) => <PkceAuthorization {...pageProps} />}
                  includeHeader={false}
                />
              )}
            />

            {/* NO SESSION PAGES */}
            <Route
              path={URLS.notFound}
              exact
              component={(props: RouteComponentProps) => (
                <Page {...props} Component={NotFoundPage} includeHeader={false} />
              )}
            />
          </Switch>
        )}
      </AppContext.Consumer>
    </AppContextProvider>
  );
};
